import { FREEFORM_COLOR_FAMILIES } from '@/app/create/components/colorFamilies';

/**
 * Target render size for the browse-by category cards (Subjects / Styles /
 * Rooms). Consistent 4:3 landscape so the whole browse-by section — tabs +
 * card row — stays compact on mobile. Source images are DB-driven
 * (artworkCategoryOptions.imageUrl, see the manifest in the shop redesign
 * notes) and are `object-cover`-cropped to this box, so keep the important
 * subject near the center of any replacement asset.
 */
export const CATEGORY_CARD_DIMENSIONS = <const>{
  aspectRatio: '4 / 3',
  mobile: { width: 92, height: 69 },
  desktop: { width: 120, height: 90 },
};

/** `sizes` attr for next/image on the browse-by cards. */
export const CATEGORY_CARD_IMAGE_SIZES = '(min-width: 1024px) 120px, 92px';

/**
 * Maps a shop "colors" taxonomy option to a CSS background (solid hex or
 * gradient) for its swatch chip. Colors are rendered as swatches, not
 * images, so this never touches `imageUrl`.
 *
 * The shop's "colors" dimension is a color *scheme* (cool / warm / pastel /
 * vibrant / monochrome — confirmed live via `/api/categories`), not
 * individual hue families, so a single flat hex per option can't represent
 * it well. Known schemes get a small curated gradient; anything else falls
 * back to the on-brand hue palette already used by the create flow's
 * color-family picker (`FREEFORM_COLOR_FAMILIES`), then to a neutral swatch.
 */
const FALLBACK_SWATCH = '#94a3b8'; // slate-400 — neutral, on-brand fallback

const normalize = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

// Curated per the live "colors" (color scheme) taxonomy: cool, warm, pastel,
// vibrant, monochrome. Keyed by normalized handle.
const COLOR_SCHEME_SWATCH: Record<string, string> = {
  warm: 'linear-gradient(135deg, #f59e0b, #ef4444)',
  cool: 'linear-gradient(135deg, #38bdf8, #0d9488)',
  pastel: 'linear-gradient(135deg, #fbcfe8, #bfdbfe)',
  vibrant: 'linear-gradient(135deg, #ec4899, #f59e0b 50%, #22d3ee)',
  monochrome: 'linear-gradient(135deg, #f8fafc, #64748b 55%, #0f172a)',
};

const FAMILY_SWATCH_BY_NORMALIZED_ID = new Map(
  FREEFORM_COLOR_FAMILIES.map(family => [normalize(family.id), family.swatch]),
);

export function resolveColorSwatch(handle: string, name: string): string {
  const normalizedHandle = normalize(handle);
  const normalizedName = normalize(name);

  const scheme = COLOR_SCHEME_SWATCH[normalizedHandle] ?? COLOR_SCHEME_SWATCH[normalizedName];
  if (scheme) return scheme;

  const exact =
    FAMILY_SWATCH_BY_NORMALIZED_ID.get(normalizedHandle) ??
    FAMILY_SWATCH_BY_NORMALIZED_ID.get(normalizedName);
  if (exact) return exact;

  // Loose match: DB color handles may be more specific than the family list
  // (e.g. "warm-red" should still resolve to the "reds" swatch).
  for (const [familyId, swatch] of FAMILY_SWATCH_BY_NORMALIZED_ID) {
    if (normalizedHandle.includes(familyId) || normalizedName.includes(familyId)) {
      return swatch;
    }
  }

  return FALLBACK_SWATCH;
}
